import type { AppNotification } from '../types';

/*
 * Короткий вид уведомления для колокольчика панели.
 *
 * Сервер пишет полный адрес прямо в текст («Наряд «620089, Россия, Свердловская область,
 * Екатеринбург, улица Академика Шварца, 17, ТЦ «Дирижабль»» выполнен»), отдельного поля
 * с адресом у уведомления нет. Старые записи в базе не переписываем, а сервер для этого
 * не трогаем (выкатывается только панель) — поэтому адрес достаём из текста здесь: он
 * всегда в «ёлочках», внутри бывают свои кавычки («ТЦ «Дирижабль»»), их считаем парами.
 * Текст, который не распознали, показывается как был — ничего не теряется.
 */

export type NotificationTone = 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'purple' | 'neutral';

export interface NotificationView {
  /** Кто сделал — мастер из «Сергушев: …» */
  who?: string;
  /** Короткий адрес наряда */
  address?: string;
  /** Что осталось от текста после адреса и имени */
  details?: string;
  /** Сколько файлов добавлено (для «Новые медиафайлы» — складываются при объединении) */
  files?: number;
}

/** Цвет и значок по типу события */
export function notificationTone(type: string): NotificationTone {
  switch (type) {
    case 'ORDER_COMPLETED':
      return 'success';
    case 'ORDER_PAUSED':
    case 'LATE':
    case 'SCHEDULE_CHANGED':
    case 'REMINDER':
    case 'ORDER_REQUEST':
      return 'warning';
    case 'ORDER_CANCELLED':
    case 'ORDER_REQUEST_DENIED':
    case 'BRIGADE_NOT_APPLIED':
    case 'WRONG_SELLER_ENTITY':
    case 'COUNTERPARTY_ALERT':
    case 'STORAGE_LOW':
      return 'danger';
    case 'MEDIA_ADDED':
    case 'MEDIA_REMOVED':
      return 'info';
    case 'CHAT_MESSAGE':
      return 'purple';
    case 'ORDER_ASSIGNED':
    case 'ORDER_UNASSIGNED':
    case 'ORDER_UPDATED':
      return 'accent';
    default:
      return 'neutral';
  }
}

/**
 * «620089, Россия, Свердловская область, Екатеринбург, улица Академика Шварца, 17» →
 * «ул. Академика Шварца, 17». Другой город (Берёзовский, Арамиль) оставляем — без него
 * адрес не найти; убираем только индекс, страну и область
 */
export function shortAddress(address: string): string {
  let s = address.trim();
  s = s.replace(/^\d{6}\s*,\s*/, '');
  s = s.replace(/^Россия\s*,\s*/i, '');
  s = s.replace(/^Свердловская\s+(обл\.?|область)\s*,\s*/i, '');
  s = s.replace(/^(г\.?\s*|город\s+)?Екатеринбург\s*[,.]\s*/i, '');
  s = s
    .replace(/(^|[\s,])улица\s/gi, '$1ул. ')
    .replace(/(^|[\s,])проспект\s/gi, '$1просп. ')
    .replace(/(^|[\s,])переулок\s/gi, '$1пер. ');
  return s || address.trim();
}

/** Все «…» верхнего уровня в строке, с учётом вложенных кавычек: [начало, конец] включительно */
function quotedSpans(text: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  let depth = 0;
  let start = -1;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '«') {
      if (depth === 0) start = i;
      depth++;
    } else if (text[i] === '»' && depth > 0) {
      depth--;
      if (depth === 0) spans.push([start, i]);
    }
  }
  return spans;
}

// Адрес отличаем от названий («Бригада 3», «Назначен мастер», «ООО …») по виду: индекс,
// страна, город или улица с номером дома
const ADDRESS_RE = /^\d{6},|Россия|Екатеринбург|(^|[\s,])(ул\.?|улица|просп\.?|проспект|пер\.?|переулок|бульвар|тракт)\s.*\d/i;

export function describeNotification(n: Pick<AppNotification, 'type' | 'body'>): NotificationView {
  let text = n.body.trim();
  const view: NotificationView = {};

  const span = quotedSpans(text).find(([a, b]) => ADDRESS_RE.test(text.slice(a + 1, b)));
  if (span) {
    view.address = shortAddress(text.slice(span[0] + 1, span[1]));
    // «наряд №1042 «…»», «Наряд «…»», «наряд: …» — слово «наряд» рядом с адресом лишнее
    const before = text.slice(0, span[0]).replace(/(наряд[ау]?|наряде)\s*(№\s*\d+\s*)?$/i, '');
    let after = text.slice(span[1] + 1);
    // Вырезали «Наряд …» в начале предложения — следующее слово становится его началом
    if (/(^|[.!?])\s*$/.test(before)) after = after.replace(/^(\s*)(\S)/, (_m, sp: string, ch: string) => sp + ch.toUpperCase());
    text = `${before}${after}`;
  } else if (n.type === 'ORDER_ASSIGNED') {
    const m = /^Вам назначен наряд:\s*(.+)$/.exec(text);
    if (m) {
      view.address = shortAddress(m[1]);
      text = '';
    }
  }

  // «Сергушев: добавлено файлов (1) — …» — имя впереди до двоеточия
  const who = /^([^:«»]{2,60}):\s+/.exec(text);
  if (who) {
    view.who = who[1].trim();
    text = text.slice(who[0].length);
  }

  if (n.type === 'MEDIA_ADDED') {
    const m = /добавлено файлов\s*\((\d+)\)/i.exec(text);
    if (m) {
      view.files = Number(m[1]);
      text = '';
    }
  } else if (n.type === 'MEDIA_REMOVED') {
    text = text.replace(/удал[её]н файл/i, '');
  } else if (n.type === 'ORDER_COMPLETED') {
    text = text.replace(/^\s*выполнен\s*$/i, '');
  }

  // Хвосты от вырезанного адреса: « — », «, », двойные пробелы
  text = text
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s—–\-,.:]+/, '')
    .replace(/[\s—–\-,:]+$/, '')
    .trim();
  if (text) view.details = text.charAt(0).toUpperCase() + text.slice(1);
  return view;
}

export function filesLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = mod10 === 1 && mod100 !== 11 ? 'файл' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'файла' : 'файлов';
  return `${n} ${word}`;
}

const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });

/** «только что», «5 мин назад», «сегодня 14:05», «вчера 00:52», «12 окт., 09:30» */
export function relativeTime(iso: string, now: number): string {
  const d = new Date(iso);
  const minutes = Math.floor((now - d.getTime()) / 60000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((today.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
  if (dayDiff <= 0) return `сегодня ${TIME.format(d)}`;
  if (dayDiff === 1) return `вчера ${TIME.format(d)}`;
  const date = d.getFullYear() === today.getFullYear() ? DAY_MONTH.format(d) : DAY_MONTH_YEAR.format(d);
  return `${date}, ${TIME.format(d)}`;
}

export interface NotificationGroup {
  /** Самое свежее уведомление группы — его время и заголовок показываем */
  first: AppNotification;
  items: AppNotification[];
  view: NotificationView;
}

/**
 * Подряд идущие одинаковые события по одному наряду («Новые медиафайлы» ×3 — мастер
 * снимает по одному фото) — одной строкой со счётчиком. Список уже от новых к старым
 */
export function groupNotifications(list: AppNotification[]): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  for (const n of list) {
    const view = describeNotification(n);
    const last = groups[groups.length - 1];
    if (last && n.orderId && last.first.orderId === n.orderId && last.first.type === n.type && last.first.title === n.title) {
      last.items.push(n);
      if (view.files != null) last.view.files = (last.view.files ?? 0) + view.files;
      if (view.who && last.view.who !== view.who) last.view.who = undefined;
      continue;
    }
    groups.push({ first: n, items: [n], view });
  }
  return groups;
}
